import { EntityRoutes } from 'routes/constants';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';

const getCurrentPath = () =>
  `${window.location.pathname}${window.location.search ?? ''}${window.location.hash ?? ''}`;

/**
 * Returns the path only when it resolves to this app's origin and is not the login page,
 * so a stored or crafted value can never redirect outside the app.
 * @param {string} path
 * @returns {string|null}
 */
const toSafeInternalPath = path => {
  if (typeof path !== 'string' || !path.startsWith('/')) return null;

  try {
    const url = new URL(path, window.location.origin);
    const safePath = `${url.pathname}${url.search}${url.hash}`;
    const decodedPathname = decodeURIComponent(url.pathname);
    // eslint-disable-next-line no-control-regex
    const hasControlCharacters = /[\u0000-\u001F\u007F]/.test(decodedPathname);
    const pointsToAnotherHost =
      hasControlCharacters || decodedPathname.startsWith('//') || decodedPathname.includes('\\');
    if (
      url.origin !== window.location.origin ||
      pointsToAnotherHost ||
      url.pathname === EntityRoutes.Login
    ) {
      return null;
    }

    return safePath;
  } catch (error) {
    return null;
  }
};

/**
 * Remembers, for this browser tab, where to return after signing in.
 * @param {string} [path] defaults to the current URL
 */
const saveReturnTo = (path = getCurrentPath()) => {
  const safePath = toSafeInternalPath(path);
  if (!safePath) return;

  sessionStorage.setItem(PERSISTENCE_KEYS.RETURN_TO, safePath);
};

/**
 * Reads and forgets the saved destination.
 * @returns {string|null}
 */
const consumeReturnTo = () => {
  const savedPath = sessionStorage.getItem(PERSISTENCE_KEYS.RETURN_TO);
  sessionStorage.removeItem(PERSISTENCE_KEYS.RETURN_TO);

  return toSafeInternalPath(savedPath);
};

export { consumeReturnTo, saveReturnTo };
